import React, { useState, useRef, useEffect } from 'react';
import type { AnchorHTMLAttributes } from 'react';
import { Popover } from '../Popover';

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick' | 'onMouseEnter' | 'onMouseLeave'> {
  /**
   * The size variant of the link (pairs text and icon sizes)
   * - 'small': 12px text and icon (default)
   * - 'medium': 14px text and icon
   * - 'large': 16px text and icon
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * The color variant of the link
   * - 'strong': Primary text color
   * - 'default': Secondary text color (default)
   * - 'subtle': Subtle gray color
   */
  color?: 'strong' | 'default' | 'subtle';
  
  /**
   * The underline style of the link
   * - 'dotted': Dotted underline
   * - 'solid': Solid underline
   * - 'none': No underline
   */
  underline?: 'dotted' | 'solid' | 'none';
  
  /**
   * The font weight of the link
   * - 'default': 400 weight (normal)
   * - 'bold': 500 weight (medium)
   * - 'heavy': 500 weight (medium) - same as bold but semantic naming
   */
  weight?: 'default' | 'bold' | 'heavy';
  
  /**
   * Response type when the link is interacted with
   * - 'none': No action taken
   * - 'link': Normal link behavior (default)
   * - 'popover': Show a popover
   */
  response?: 'none' | 'link' | 'popover';
  
  /**
   * Action type that triggers the response
   * - 'onclick': Trigger on click (default)
   * - 'onhover': Trigger on hover
   */
  actionType?: 'onclick' | 'onhover';
  
  /**
   * Icon component to display alongside the link text
   * The icon will inherit the same color and hover effects as the text
   */
  icon?: React.ComponentType<any> | React.ReactNode;
  
  /**
   * Position of the icon relative to the text
   * - 'left': Show icon before the text
   * - 'right': Show icon after the text (default)
   */
  iconPosition?: 'left' | 'right';
  
  /**
   * Size of the icon in pixels
   * @deprecated Use 'size' prop instead to pair text and icon sizes
   */
  iconSize?: number;
  
  /**
   * Show icon only on hover
   * - true: Icon appears only when hovering
   * - false: Icon always visible (default)
   */
  iconOnHover?: boolean;
  
  /**
   * Popover subtitle (optional, displayed above main content)
   */
  popoverSubtitle?: React.ReactNode | string;
  
  /**
   * Popover main content (required if response is 'popover')
   */
  popoverContent?: React.ReactNode | string;
  
  /**
   * Popover state message (optional, displayed below main content)
   */
  popoverState?: React.ReactNode | string;
  
  /**
   * Type of popover state message
   */
  popoverStateType?: 'default' | 'error' | 'success' | 'warning' | 'info';
  
  /**
   * Color variant for the popover
   */
  popoverVariant?: 'default' | 'subtle' | 'strong';
  
  /**
   * Position of the popover relative to the link
   * - 'above': Show above the link
   * - 'below': Show below the link (default)
   * - 'left': Show to the left
   * - 'right': Show to the right
   */
  popoverPosition?: 'above' | 'below' | 'left' | 'right';
  
  /**
   * Custom onClick handler (only works when response is 'link' or 'none')
   */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  
  /**
   * Custom onHover handler
   */
  onHover?: () => void;
  
  /**
   * Children content
   */
  children: React.ReactNode;
  
  /**
   * Additional CSS classes
   */
  className?: string;
}

export function Link({
  size = 'small',
  color = 'default',
  underline = 'dotted',
  weight = 'bold',
  response = 'link',
  actionType = 'onclick',
  icon,
  iconPosition = 'right',
  iconSize,
  iconOnHover = false,
  popoverSubtitle,
  popoverContent,
  popoverState,
  popoverStateType = 'default',
  popoverVariant = 'default',
  popoverPosition = 'below',
  children,
  className = '',
  href = '#',
  onClick,
  onHover,
  ...rest
}: LinkProps) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const hoverTimeoutRef = useRef<number | null>(null);
  
  // Determine icon size based on size variant (can be overridden by iconSize prop)
  const getIconSize = () => {
    if (iconSize) return iconSize; // Allow override for backward compatibility
    switch (size) {
      case 'small': return 12;
      case 'medium': return 14;
      case 'large': return 16;
      default: return 12;
    }
  };
  
  const computedIconSize = getIconSize();
  
  const linkClasses = [
    'core-link',
    `core-link--${size}`, // Add size class
    `core-link--${color}`,
    `core-link--underline-${underline}`,
    `core-link--weight-${weight}`,
    response === 'none' ? 'core-link--no-action' : '',
    response === 'popover' ? 'core-link--popover' : '',
    icon ? 'core-link--with-icon' : '',
    icon && iconPosition === 'left' ? 'core-link--icon-left' : '',
    icon && iconPosition === 'right' ? 'core-link--icon-right' : '',
    icon && iconOnHover ? 'core-link--icon-on-hover' : '',
    className
  ].filter(Boolean).join(' ');
  
  // Handle click events
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (response === 'none') {
      e.preventDefault();
      return;
    }
    
    if (response === 'popover' && actionType === 'onclick') {
      e.preventDefault();
      setIsPopoverVisible(!isPopoverVisible);
      return;
    }
    
    if (response === 'link' && onClick) {
      onClick(e);
    }
  };
  
  // Handle hover events
  const handleMouseEnter = () => {
    if (onHover) {
      onHover();
    }
    
    if (response === 'popover' && actionType === 'onhover') {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Show popover immediately
      setIsPopoverVisible(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (response === 'popover' && actionType === 'onhover') {
      // Clear any pending show timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Hide popover with delay to allow moving to popover
      hoverTimeoutRef.current = window.setTimeout(() => {
        setIsPopoverVisible(false);
      }, 300);
    }
  };
  
  // Handle popover mouse events (keep it open when hovering over popover)
  const handlePopoverMouseEnter = () => {
    if (actionType === 'onhover' && hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };
  
  const handlePopoverMouseLeave = () => {
    if (actionType === 'onhover') {
      hoverTimeoutRef.current = window.setTimeout(() => {
        setIsPopoverVisible(false);
      }, 300);
    }
  };
  
  // Close popover on click outside
  useEffect(() => {
    if (isPopoverVisible && actionType === 'onclick') {
      const handleClickOutside = (e: MouseEvent) => {
        if (!linkRef.current?.contains(e.target as Node)) {
          setIsPopoverVisible(false);
        }
      };
      
      // Add a small delay to avoid immediately closing on the click that opened it
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isPopoverVisible, actionType]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // Render the icon element
  const renderIcon = () => {
    if (!icon) return null;
    
    // If it's a React element, clone it with the size prop
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<any>, { 
        size: computedIconSize,
        className: 'core-link__icon'
      });
    }
    
    // If it's a component (function or ForwardRef), render it with the size prop
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<any>;
      return <IconComponent size={computedIconSize} className="core-link__icon" />;
    }
    
    // Handle ForwardRef components (which are objects with $$typeof and render)
    // ForwardRef components can be treated as regular components
    if (typeof icon === 'object' && icon && '$$typeof' in icon && 'render' in icon) {
      const IconComponent = icon as any; // Use any to bypass TS check for ForwardRef
      return <IconComponent size={computedIconSize} className="core-link__icon" />;
    }
    
    return null;
  };
  
  const iconElement = renderIcon();
  
  return (
    <>
      <a
        ref={linkRef}
        className={linkClasses}
        href={response === 'none' ? undefined : href}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...rest}
      >
        {iconPosition === 'left' && iconElement}
        <span className="core-link__text">{children}</span>
        {iconPosition === 'right' && iconElement}
      </a>
      
      {response === 'popover' && popoverContent && (
        <Popover
          isVisible={isPopoverVisible}
          targetRef={linkRef}
          subtitle={popoverSubtitle}
          content={popoverContent}
          state={popoverState}
          stateType={popoverStateType}
          variant={popoverVariant}
          position={popoverPosition}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        />
      )}
    </>
  );
}

export default Link;
